import { parseAndMergeAndConvert, isFeatureSupported } from '../../common/utils';
import {
    generateFreestyleSchemaV4,
    generateListReportSchemaV4,
    generateObjectPageSchemaV4,
    generateMacrosSchemaDefinition
} from '.';
import type {
    ExtensionLogger,
    GenerateAppSchemaParameters,
    FileData,
    Manifest,
    FeatureToggle
} from '@sap/ux-specification-types';
import { v4, FioriElementsVersion, TemplateType, Features } from '@sap/ux-specification-types';
import { getPageCustomViewFile } from '../utils/utils';
import { getEntityTypeEntitySetFromContextPath } from '../../common/importProject';
import { initI18n } from '../../../i18n/i18n';

/**
 * Generates the app specific schema of an object page out of the generic schema for the same page.
 * Generic types are replaced by information from the app specific annotations.
 *
 * @param genericSchema - generic JSON schema of a list report
 * @param entitySetName - the base entity set of the given page
 * @param annotations - list of all annotation files (file content plus URI as identifier)
 * @param manifest - manifest.json of the app
 * @param {ExtensionLogger} logger - Logger class for logging messages
 * @param {FileData[]} fragments - list of fragment files and their content
 * @param {string} contextPath - contextPath of the actual (object) page
 * @param {object} genericMacrosSchema - generic macros schema of the app
 * @param {FeatureToggle[]} [featureToggles] - array of supported features
 * @returns the app specific JSON schema
 */
export function generateObjectPageInterfaceV4(
    genericSchema: object,
    entitySetName: string,
    annotations: FileData[],
    manifest: Manifest,
    logger?: ExtensionLogger,
    fragments?: FileData[],
    contextPath?: string,
    genericMacrosSchema?: object,
    featureToggles?: FeatureToggle[]
): object {
    const serviceAVT = parseAndMergeAndConvert(annotations, logger);

    const { entitySet, entityType } = getEntityTypeEntitySetFromContextPath(
        serviceAVT,
        contextPath,
        entitySetName,
        logger
    );

    const generateParameters: GenerateAppSchemaParameters = {
        templateType: TemplateType.ListReportObjectPageV4,
        fioriElementsVersion: FioriElementsVersion.v4,
        manifest,
        fragments,
        serviceAVT,
        logger,
        entitySet,
        entityType,
        featureToggles
    };
    return generateObjectPageSchemaV4(generateParameters, genericSchema, contextPath, genericMacrosSchema);
}

/**
 * Generates the app specific schema of a list report out of the generic schema.
 * Generic types are replaced by information from the app specific annotations.
 *
 * @param {TemplateType} templateType - the template type allows distinguishing LR and ALP
 * @param genericSchema - generic JSON schema of a list report
 * @param entitySetName - the base entity set of the given page
 * @param annotations - list of all annotation files (file content plus URI as identifier)
 * @param {ExtensionLogger} logger - Logger class for logging messages
 * @param manifest - manifest.json of the app
 * @param contextPath - the context path of the actual page
 * @returns the app specific JSON schema
 */
export function generateListReportInterfaceV4(
    templateType: TemplateType,
    genericSchema: object,
    entitySetName: string,
    annotations: FileData[],
    logger?: ExtensionLogger,
    manifest?: Manifest,
    contextPath?: string
): object {
    initI18n();
    const serviceAVT = parseAndMergeAndConvert(annotations, logger);
    const { entitySet, entityType } = getEntityTypeEntitySetFromContextPath(
        serviceAVT,
        contextPath,
        entitySetName,
        logger
    );
    const generateParameters: GenerateAppSchemaParameters = {
        templateType,
        fioriElementsVersion: FioriElementsVersion.v4,
        manifest,
        serviceAVT,
        logger,
        entitySet,
        entityType
    };

    const templateName =
        templateType === TemplateType.ListReportObjectPageV4 ? v4.FE_TEMPLATE_V4_LIST_REPORT : v4.FE_TEMPLATE_V4_ALP;
    return generateListReportSchemaV4(generateParameters, genericSchema, templateName, contextPath);
}

/**
 * Generates the app specific schema of a freestyle page out of the generic schema.
 * Generic types are replaced by information from the app specific annotations.
 *
 * @param templateType - the template type allows distinguishing LR and ALP
 * @param genericSchema - generic JSON schema of a list report
 * @param annotations - list of all annotation files (file content plus URI as identifier)
 * @param manifest - manifest.json of the app
 * @param _entitySetName - the name of the entity set (optional)
 * @param logger - Logger class for logging messages
 * @returns the app specific JSON schema
 */
export function generateFreestyleInterfaceV4(
    templateType: TemplateType,
    genericSchema: object,
    annotations: FileData[],
    manifest?: Manifest,
    _entitySetName?: string,
    logger?: ExtensionLogger
): object {
    const serviceAVT = parseAndMergeAndConvert(annotations, logger);
    const generateParameters: GenerateAppSchemaParameters = {
        templateType,
        fioriElementsVersion: FioriElementsVersion.v4,
        manifest,
        serviceAVT,
        logger
    };
    return generateFreestyleSchemaV4(generateParameters, genericSchema);
}

/**
 * Generates the app specific schema of a FPM Custom page out of the generic schema.
 *
 * @param genericSchema - generic JSON schema of a list report.
 * @param routingId - page id in routing.
 * @param manifest - manifest.json of the app.
 * @param views - array of views.xml files.
 * @param annotations - list of all annotation files (file content plus URI as identifier)
 * @param [featureToggles] - array of supported features.
 * @param [logger] - Logger class for logging messages.
 * @returns the app specific JSON schema.
 */
export function generateFPMCustomPageInterfaceV4(
    genericSchema: object,
    routingId: string,
    manifest: Manifest,
    views: FileData[],
    annotations: FileData[],
    featureToggles?: FeatureToggle[],
    logger?: ExtensionLogger
): object {
    let schema = {};
    if (isFeatureSupported(featureToggles, Features.BuildingBlocks)) {
        const serviceAVT = parseAndMergeAndConvert(annotations, logger);
        const viewData = getPageCustomViewFile(views || [], routingId, manifest, logger);
        schema = viewData?.file.fileContent
            ? generateMacrosSchemaDefinition(genericSchema, viewData, serviceAVT, logger)
            : {};
    }
    return schema;
}
